import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import ordersService from '../../services/orders.service';
import { cachedGet } from '../../utils/lookupCache';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import { metricsDateShortcuts } from './dispatcherMetrics/dateShortcuts';
import FilterBar from '../../components/FilterBar';
import { expandLcs, metricFilterOptions, MetricFilterRow } from './dispatcherMetrics/metricFilters';
import MetricMatrixTable from './dispatcherMetrics/MetricMatrixTable';
import OrderMetricSelector, { MetricGroup, storedGroup, groupLabel, groupedOrderRows, groupedMetric } from './dispatcherMetrics/OrderMetricSelector';
import SchedulingSnapshot from './dispatcherMetrics/SchedulingSnapshot';
import LcAdoptionChart from './dispatcherMetrics/LcAdoptionChart';
import LcV1SavesModal, { V1SavesLc } from './dispatcherMetrics/LcV1SavesModal';
import FinalizeDetailModal from './dispatcherMetrics/FinalizeDetailModal';
import { KEY_STATUS_OPTIONS } from '../order-management/quoteColors';
import { SERVICE_FAILURE_TYPE_FILTER_OPTIONS } from '../../constants/serviceFailureTypes';
import DetailModal, { DetailKind } from './dispatcherMetrics/DetailModal';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import RescheduleEditModal from '../pro/components/RescheduleEditModal';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import {
  MetricsResponse,
  MetricDef,
  LC_SERVICE_FAILURES_METRIC,
  LC_UNCODED_METRIC,
  LC_OTD_METRIC,
  LC_CUSTOMER_OTD_METRIC,
  LC_PASS_METRIC,
  LC_CUSTOMER_PASS_METRIC,
  LC_CUSTOMER_SERVICE_FAILURES_METRIC,
  LC_CUSTOMER_UNCODED_METRIC,
  SCHED_DELIVERIES_METRIC,
  SCHED_AVG_TIME_ON_LC_METRIC,
  ScheduleSnapshotRow,
  LcUtilizationRow,
  AnyRow,
  ServiceFailureDetail,
  LateDetail,
  UncodedDetail,
  ApptFinalizedDetail,
  serviceFailureDetails,
  serviceFailurePros,
  lateDetailRows,
  latePros,
  uncodedDetailRows,
  uncodedPros,
  apptFinalizedDetails,
  apptFinalizedPros,
  passFailDetails,
  passFailPros,
  buildView,
  filterRowsByFailureType,
  filterRowsByReasonScope,
  ReasonScope,
  withFailureTypeFilter,
  OrderRow,
  bucketSignature,
  fmtDateKey,
  fmtMD,
  parseLocal,
  isDateKey,
  FinalizeCell,
} from './dispatcherMetrics/logic';

/**
 * LC Metrics report — the order-side OTD / Service-Failure / Uncoded tables
 * keyed on the order's LC (Load Coordinator = ordersall.csr) instead of the
 * dispatcher, alongside the shared OTD-by-Customer tables.
 *
 * Reuses the same endpoint (/reporting/dispatcher-metrics — which now returns an
 * lc_rows collection) and the same view-model + table/modal components as the
 * Dispatcher Metrics report; only the axis (and the metric tab set) differ.
 */

// 'schedule' tabs are per-day matrix tables backed by schedule_rows; 'snapshot'
// is the current-pipeline panel; 'utilization' is the V1-vs-360 adoption chart.
// The last two carry no MetricDef / no date columns.
type ViewKind = 'lc' | 'customer' | 'schedule' | 'snapshot' | 'utilization';
interface MetricTab {
  key: string;
  label: string;
  group: string;
  kind: ViewKind;
  metric?: MetricDef;     // absent for the snapshot tab
  description?: string;   // shown when there's no metric to carry it
}
const SNAPSHOT_TAB_KEY = 'lc_pipeline';
const UTILIZATION_TAB_KEY = 'lc_utilization';
const METRIC_TABS: MetricTab[] = [
  // Scheduling group (leftmost): per-day throughput + the current snapshot.
  { key: SCHED_DELIVERIES_METRIC.key, label: SCHED_DELIVERIES_METRIC.label, group: 'Scheduling', metric: SCHED_DELIVERIES_METRIC, kind: 'schedule' as const },
  { key: SCHED_AVG_TIME_ON_LC_METRIC.key, label: SCHED_AVG_TIME_ON_LC_METRIC.label, group: 'Scheduling', metric: SCHED_AVG_TIME_ON_LC_METRIC, kind: 'schedule' as const },
  { key: SNAPSHOT_TAB_KEY, label: 'Current Snapshot', group: 'Scheduling', kind: 'snapshot' as const, description: 'Current open scheduling backlog per LC: Pending LC / Avg Time (live clock) / Pending Customer / Hold (point-in-time; the date range does not apply).' },
  // Adoption group: per-LC GWI 360 share of saves over the selected range.
  { key: UTILIZATION_TAB_KEY, label: '360 Adoption', group: 'Adoption', kind: 'utilization' as const, description: "Each LC's GWI 360 share of saves over the selected date range. Every edit made anywhere in CMS counts (from cms.history), once per save; scheduled scripts and system rows are excluded. Click an LC under 100% to see what they saved in V1." },
  // LC order-side metrics.
  { key: LC_SERVICE_FAILURES_METRIC.key, label: LC_SERVICE_FAILURES_METRIC.label, group: 'LC', metric: LC_SERVICE_FAILURES_METRIC, kind: 'lc' as const },
  { key: LC_PASS_METRIC.key, label: LC_PASS_METRIC.label, group: 'LC', metric: LC_PASS_METRIC, kind: 'lc' as const },
  { key: LC_UNCODED_METRIC.key, label: LC_UNCODED_METRIC.label, group: 'LC', metric: LC_UNCODED_METRIC, kind: 'lc' as const },
  { key: LC_OTD_METRIC.key, label: LC_OTD_METRIC.label, group: 'LC', metric: LC_OTD_METRIC, kind: 'lc' as const },
  // Customer group mirrors the LC order: Service Failures / Service % / Uncoded / OTD.
  { key: LC_CUSTOMER_SERVICE_FAILURES_METRIC.key, label: LC_CUSTOMER_SERVICE_FAILURES_METRIC.label, group: 'Customer', metric: LC_CUSTOMER_SERVICE_FAILURES_METRIC, kind: 'customer' as const },
  { key: LC_CUSTOMER_PASS_METRIC.key, label: LC_CUSTOMER_PASS_METRIC.label, group: 'Customer', metric: LC_CUSTOMER_PASS_METRIC, kind: 'customer' as const },
  { key: LC_CUSTOMER_UNCODED_METRIC.key, label: LC_CUSTOMER_UNCODED_METRIC.label, group: 'Customer', metric: LC_CUSTOMER_UNCODED_METRIC, kind: 'customer' as const },
  { key: LC_CUSTOMER_OTD_METRIC.key, label: LC_CUSTOMER_OTD_METRIC.label, group: 'Customer', metric: LC_CUSTOMER_OTD_METRIC, kind: 'customer' as const },
];
const SELECTABLE_TABS = METRIC_TABS.filter((t) => t.kind !== 'customer').map((t) => ({ ...t, group: t.group === 'LC' ? 'Orders' : t.group, label: t.label.replace('OTD by LC', 'On Time Delivery') }));

// Each tab is served by one backend "section", so the page fetches them in
// parallel (one request per section, ?sections=…) and paints each panel as it
// returns — the cheap Current Snapshot no longer waits on the Fabric OTD scan
// or the cms.history adoption scan. The LC + Customer tabs share the 'otd'
// bundle (both come off the same Fabric fetch, so splitting them would re-run
// it). See get_dispatcher_metrics on the backend.
type SectionKey = 'otd' | 'schedule' | 'snapshot' | 'utilization';
const SECTION_FOR_KIND: Record<ViewKind, SectionKey> = {
  snapshot: 'snapshot',
  schedule: 'schedule',
  utilization: 'utilization',
  lc: 'otd',
  customer: 'otd',
};
const ALL_LOADING = (): Record<SectionKey, boolean> =>
  ({ otd: true, schedule: true, snapshot: true, utilization: true });
const NO_ERROR = (): Record<SectionKey, string | null> =>
  ({ otd: null, schedule: null, snapshot: null, utilization: null });

// Service % / Service Failures reason scope: 'cs_only' narrows the metric to
// just "C: Customer Service Issue" reschedules instead of every controllable
// (C:) reason across every View by grouping — a pure client-side filter
// (filterRowsByReasonScope), no server round trip.
const SERVICE_PASS_SCOPE_OPTIONS: { value: ReasonScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'cs_only', label: 'CS Only' },
];

// Persisted control-bar state (distinct key so it never clobbers the dispatcher
// report's or the Home card's).
const FILTERS_KEY = 'lcMetricsReportFilters';
interface StoredFilters {
  regions?: string[];
  customers?: string[];
  deliveryLocations?: string[];
  excludeWeekends?: boolean;
  showDetails?: boolean;
  startDate?: string;
  endDate?: string;
  selectedLcs?: string[];
  viewMode?: 'daily' | 'weekly';
  metric?: string;
  grouping?: MetricGroup;
  keyStatuses?: string[];
  sfTypes?: string[];
  adoptionGroup?: 'lcs' | 'invoice_clerks';
  servicePassScope?: ReasonScope;
}
const loadStoredFilters = (): StoredFilters => {
  try {
    const parsed = JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const defaultStartDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d;
};
// Default end date is yesterday — today is still in progress, so the range
// defaults to complete days. (The user can still extend it to today.)
const defaultEndDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};
// Latest selectable date is today — future dates stay excluded, but the range
// may be extended into the current (in-progress) day.
const maxSelectableDate = (): Date => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const LCMetricsContent: React.FC = () => {
  const [stored] = useState(loadStoredFilters);
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(
    Array.isArray(stored.regions) ? stored.regions : []
  );
  useEffect(() => {
    let active = true;
    ordersService.getRegions().then((res: any) => {
      if (!active) return;
      const regions: string[] = res?.data?.regions || res?.regions || [];
      setRegionOptions(regions.map((r) => ({ value: r, label: r })));
    }).catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(Array.isArray(stored.customers) ? stored.customers : []);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(Array.isArray(stored.deliveryLocations) ? stored.deliveryLocations : []);
  const [filterRows, setFilterRows] = useState<MetricFilterRow[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(false);
  const [adoptionGroup, setAdoptionGroup] = useState<'lcs' | 'invoice_clerks'>(
    stored.adoptionGroup === 'invoice_clerks' ? 'invoice_clerks' : 'lcs'
  );
  // The adoption chart's SVG text follows the Display Preferences font size.
  const { appFontSizePx } = useDisplayPreferences();
  const rootFontPx = appFontSizePx ?? (parseFloat(getComputedStyle(document.documentElement).fontSize) || 17);
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const [startDate, setStartDate] = useState<Date | null>(() =>
    isDateKey(stored.startDate) ? parseLocal(stored.startDate) : defaultStartDate()
  );
  const [endDate, setEndDate] = useState<Date | null>(() =>
    isDateKey(stored.endDate) ? parseLocal(stored.endDate) : defaultEndDate()
  );
  const dateShortcuts = useMemo(() => metricsDateShortcuts((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }), []);
  const [excludeWeekends, setExcludeWeekends] = useState(stored.excludeWeekends === true);
  const [showDetails, setShowDetails] = useState(stored.showDetails === true);
  const [viewMode, setViewMode] = useState<'daily' | 'weekly'>(
    stored.viewMode === 'weekly' ? 'weekly' : 'daily'
  );
  // LC filter (include): empty = all LCs. Applies to the LC + Scheduling tables
  // and the snapshot, and scopes every order grouping through the server.
  const [selectedLcs, setSelectedLcs] = useState<string[]>(
    Array.isArray(stored.selectedLcs) ? stored.selectedLcs : []
  );
  // Order-only filters: scheduling and adoption are scoped solely by LC.
  // Empty key status means all customers.
  const [selectedKeyStatuses, setSelectedKeyStatuses] = useState<string[]>(
    Array.isArray(stored.keyStatuses) ? stored.keyStatuses : []
  );
  const [grouping, setGrouping] = useState<MetricGroup>(() => storedGroup(stored.grouping,
    stored.metric?.includes('customer') ? 'customer' : 'lc'));
  // Service-failure types to count (empty = all). Only the Service Failures
  // tables read it, so the control shows only while one of them is open.
  const [selectedSfTypes, setSelectedSfTypes] = useState<string[]>(
    Array.isArray(stored.sfTypes) ? stored.sfTypes : []
  );
  // Shared reason scope for Service % and Service Failures in every grouping.
  const [servicePassScope, setServicePassScope] = useState<ReasonScope>(
    stored.servicePassScope === 'cs_only' ? 'cs_only' : 'all'
  );
  const [selectedMetric, setSelectedMetric] = useState<string>(
    (() => {
      const saved = METRIC_TABS.find((t) => t.key === stored.metric);
      return saved?.kind === 'customer'
        ? SELECTABLE_TABS.find((t) => t.metric?.detailKind === saved.metric?.detailKind)?.key ?? SELECTABLE_TABS[0].key
        : saved?.key ?? SELECTABLE_TABS[0].key;
    })()
  );

  // Service Failures / OTD detail modal + Pro drawer.
  const [detailModal, setDetailModal] = useState<{ kind: DetailKind; title: string; rows: (ServiceFailureDetail | LateDetail | UncodedDetail | ApptFinalizedDetail)[]; pros: number[] } | null>(null);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  // New Service Failure record (from an Uncoded planned date), seeded with the pro + planned date.
  const [sfCreate, setSfCreate] = useState<
    { pro: number; date: string; billto?: number | null } | null
  >(null);
  const openDetail = useCallback((rows: AnyRow[], kind: DetailKind) => {
    const names = Array.from(new Set(rows.map((r) => r.dispatcher).filter(Boolean)));
    const dates = Array.from(new Set(rows.map((r) => r.date)));
    const who = names.length === 1 ? names[0] : '';
    const when = dates.length === 1 ? fmtMD(dates[0]) : '';
    const detailRows = kind === 'otd'
      ? lateDetailRows(rows)
      : kind === 'uncoded'
        ? uncodedDetailRows(rows)
        : kind === 'apptsFinalized'
          ? apptFinalizedDetails(rows)
          : kind === 'servicePass'
            ? passFailDetails(rows)
            : serviceFailureDetails(rows);
    const pros = kind === 'otd'
      ? Array.from(new Set(latePros(rows)))
      : kind === 'uncoded'
        ? uncodedPros(rows)
        : kind === 'apptsFinalized'
          ? apptFinalizedPros(rows)
          : kind === 'servicePass'
            ? passFailPros(rows)
            : serviceFailurePros(rows);
    setDetailModal({ kind, title: [who, when].filter(Boolean).join(', '), rows: detailRows, pros });
  }, []);

  const maxDate = useMemo(maxSelectableDate, []);

  const [data, setData] = useState<MetricsResponse | null>(null);
  // Per-section load state so each panel paints independently as its request
  // returns (parallel fetches below), instead of blocking on the slowest.
  const [sectionLoading, setSectionLoading] = useState<Record<SectionKey, boolean>>(ALL_LOADING);
  const [sectionError, setSectionError] = useState<Record<SectionKey, string | null>>(NO_ERROR);
  const abortRef = useRef<AbortController | null>(null);

  // Persist the control bar.
  useEffect(() => {
    try {
      const filters: StoredFilters = {
        regions: selectedRegions,
        customers: selectedCustomers,
        deliveryLocations: selectedLocations,
        excludeWeekends,
        showDetails,
        startDate: startDate ? fmtDateKey(startDate) : undefined,
        endDate: endDate ? fmtDateKey(endDate) : undefined,
        selectedLcs,
        viewMode,
        metric: selectedMetric,
        grouping,
        keyStatuses: selectedKeyStatuses,
        sfTypes: selectedSfTypes,
        adoptionGroup,
        servicePassScope,
      };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    } catch { /* storage unavailable — non-fatal */ }
  }, [selectedRegions, selectedCustomers, selectedLocations, excludeWeekends, showDetails, startDate, endDate, selectedLcs, viewMode, selectedMetric, selectedKeyStatuses, grouping, selectedSfTypes, adoptionGroup, servicePassScope]);

  const resetFilters = () => {
    setSelectedRegions([]);
    setSelectedCustomers([]);
    setSelectedLocations([]);
    setExcludeWeekends(false);
    setShowDetails(false);
    setViewMode('daily');
    setStartDate(defaultStartDate());
    setEndDate(defaultEndDate());
    setSelectedLcs([]);
    setSelectedKeyStatuses([]);
    setSelectedSfTypes([]);
    setAdoptionGroup('lcs');
    setServicePassScope('all');
  };

  // Each panel is fetched in its own parallel request (?sections=…).
  // Order filters apply only to OTD/SF/Service %/Uncoded; LC scopes scheduling too.
  // and merged into `data` as it lands, so the cheap Current Snapshot paints
  // without waiting on the Fabric OTD scan ('otd') or the cms.history adoption
  // scan ('utilization'). The All/CS Only toggle is a pure client-side filter
  // (every controllable reschedule's `reason` already rides along in the
  // payload — see filterRowsByReasonScope) — it never touches this fetch.
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSectionLoading((prev) => ({ ...ALL_LOADING(), utilization: prev.utilization }));
    setSectionError((prev) => ({ ...NO_ERROR(), utilization: prev.utilization }));

    const base: Record<string, string> = { start_date: fmtDateKey(startDate), end_date: fmtDateKey(endDate) };
    const orderFilters: Record<string, string> = {};
    if (selectedRegions.length > 0) orderFilters.region = selectedRegions.join(',');
    if (selectedCustomers.length) orderFilters.customers = selectedCustomers.join(',');
    if (selectedLocations.length) orderFilters.delivery_locations = selectedLocations.join(',');
    if (selectedLcs.length) base.lcs = expandLcs(selectedLcs).join(',');
    // Customer key status; omitted = all customers.
    if (selectedKeyStatuses.length > 0) orderFilters.key_status = selectedKeyStatuses.join(',');
    const fetchSection = async (section: SectionKey, extra?: Record<string, string>) => {
      try {
        const res = await api.get('/reporting/dispatcher-metrics', {
          params: { ...base, sections: section, ...extra },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        // Merge this section's keys into the accumulating payload; siblings only
        // return their own keys, so this never clobbers another panel's data.
        setData((prev) => ({ ...(prev ?? {}), ...res.data }) as MetricsResponse);
      } catch (e: any) {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          setSectionError((prev) => ({ ...prev, [section]: 'Failed to load' }));
        }
      } finally {
        if (abortRef.current === controller) {
          setSectionLoading((prev) => ({ ...prev, [section]: false }));
        }
      }
    };

    // Fire all panels concurrently — the snapshot is the cheapest and lands first.
    // include_lc is what makes the endpoint compute the LC-only sections.
    //
    // One view of this page is one run, but it is four parallel requests, so all
    // but one opt out of the run audit and the OTD request records it. That is
    // deliberately an include_lc request: the endpoint is shared with the
    // Dispatcher Metrics page and include_lc is what tells the two runs apart
    // (sections=otd never computes the utilization scan).
    void Promise.all([
      fetchSection('snapshot', { background: 'true' }),
      fetchSection('schedule', { background: 'true' }),
      fetchSection('otd', { include_lc: '1', ...orderFilters }),
    ]);
  }, [startDate, endDate, selectedRegions, selectedKeyStatuses, selectedLcs, selectedCustomers, selectedLocations]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  // Fetch the unfiltered dimension tuples once per date range. Facets are then
  // recomputed locally, so each selection immediately narrows the other pickers.
  useEffect(() => {
    if (!startDate || !endDate) return;
    const controller = new AbortController();
    setFiltersLoading(true);
    setFiltersError(false);
    api.get('/reporting/dispatcher-metrics', {
      params: { start_date: fmtDateKey(startDate), end_date: fmtDateKey(endDate), sections: 'filter_options', background: 'true' },
      signal: controller.signal,
    }).then(res => {
      if (!controller.signal.aborted) setFilterRows(res.data.filter_rows ?? []);
    }).catch(() => {
      if (!controller.signal.aborted) setFiltersError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setFiltersLoading(false);
    });
    return () => controller.abort();
  }, [startDate, endDate]);

  // The roster chips refetch adoption only; other metrics keep their LC scope.
  useEffect(() => {
    if (!startDate || !endDate) return;
    const controller = new AbortController();
    setSectionLoading((prev) => ({ ...prev, utilization: true }));
    setSectionError((prev) => ({ ...prev, utilization: null }));
    api.get('/reporting/dispatcher-metrics', {
      params: {
        start_date: fmtDateKey(startDate), end_date: fmtDateKey(endDate),
        sections: 'utilization', include_lc: '1', background: 'true',
        adoption_group: adoptionGroup,
      },
      signal: controller.signal,
    }).then((res) => {
      if (!controller.signal.aborted) {
        setData((prev) => ({ ...(prev ?? {}), ...res.data }) as MetricsResponse);
      }
    }).catch((e) => {
      if (!controller.signal.aborted && e?.code !== 'ERR_CANCELED') {
        setSectionError((prev) => ({ ...prev, utilization: 'Failed to load' }));
      }
    }).finally(() => {
      if (!controller.signal.aborted) setSectionLoading((prev) => ({ ...prev, utilization: false }));
    });
    return () => controller.abort();
  }, [startDate, endDate, adoptionGroup]);

  // Adoption drill-down: the LC whose V1 saves are open (null = closed).
  const [v1SavesLc, setV1SavesLc] = useState<V1SavesLc | null>(null);
  // Pipeline drill-down: the "not finalized" cell that is open (null = closed).
  const [finalizeCell, setFinalizeCell] = useState<FinalizeCell | null>(null);
  const pickerRange = useMemo<[string, string] | null>(
    () => (startDate && endDate ? [fmtDateKey(startDate), fmtDateKey(endDate)] : null),
    [startDate, endDate]
  );

  // LC dropdown options: individual LCs + LC groups, from the shared LC-dashboard
  // filter-options endpoint (same source as the Scheduler / At-Risk pages). Each
  // group option's value is a tilde-delimited member-username list. Falls back to
  // the LCs present in the loaded data if that endpoint is unavailable.
  const [lcFilterOptions, setLcFilterOptions] = useState<MultiSelectOption[]>([]);
  useEffect(() => {
    let active = true;
    cachedGet('/lc-dashboards/filter-options')
      .then((res: any) => {
        if (!active) return;
        setLcFilterOptions((res?.data?.lcs || []).map((o: any) => ({ value: o.value, label: o.label })));
      })
      .catch(() => { /* non-fatal — fall back to data-derived options */ });
    return () => { active = false; };
  }, []);

  const lcOptions = useMemo<MultiSelectOption[]>(() => {
    if (lcFilterOptions.length > 0) return lcFilterOptions;
    // Fallback: individual LCs present in the loaded data, keyed by username.
    const seen = new Map<string, string>();
    for (const r of data?.lc_rows ?? []) seen.set(r.dispatcher_key, r.dispatcher);
    for (const r of data?.schedule_rows ?? []) seen.set(r.dispatcher_key, r.dispatcher);
    for (const r of data?.schedule_snapshot ?? []) seen.set(r.dispatcher_key, r.dispatcher);
    for (const r of data?.lc_utilization ?? []) seen.set(r.dispatcher_key, r.dispatcher);
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
  }, [lcFilterOptions, data]);

  const linkedOptions = useMemo(() => metricFilterOptions(filterRows, {
    regions: selectedRegions, lcs: selectedLcs, keyStatuses: selectedKeyStatuses,
    customers: selectedCustomers, locations: selectedLocations,
  }, lcOptions), [filterRows, selectedRegions, selectedLcs, selectedKeyStatuses, selectedCustomers, selectedLocations, lcOptions]);

  // Derived views. selectedLcs holds option VALUES — individual LC usernames
  // and/or tilde-delimited group member lists; expand them into a username set
  // (empty = all LCs). Scopes every LC-keyed table + the adoption chart (matched
  // on the LC username / dispatcher_key); other order axes are scoped by the server.
  const includeUsernames = useMemo(() => {
    const s = new Set<string>();
    for (const v of selectedLcs) for (const u of v.split('~')) if (u) s.add(u);
    return s;
  }, [selectedLcs]);
  const keepLc = useCallback(
    (usernameKey: string) => includeUsernames.size === 0 || includeUsernames.has(usernameKey),
    [includeUsernames]
  );
  const weekly = viewMode === 'weekly';
  const currentTab = METRIC_TABS.find((t) => t.key === selectedMetric) ?? METRIC_TABS[0];
  // The SF Type filter narrows the failures a Service Failures table counts, so
  // it only means anything on those tabs — applied here rather than in the fetch
  // because every failure's type already rides along in the payload.
  const isServiceFailuresTab = currentTab.metric?.detailKind === 'serviceFailures';
  const activeGrouping = grouping;
  // The reason-scope toggle applies to Service % and Service Failures.
  const isServicePassTab = currentTab.metric?.detailKind === 'servicePass';
  const showServicePassScopeToggle = isServicePassTab || isServiceFailuresTab;
  const narrowToSfTypes = useCallback(
    (rows: OrderRow[]): OrderRow[] =>
      isServiceFailuresTab && selectedSfTypes.length > 0
        ? (filterRowsByFailureType(rows, selectedSfTypes) as OrderRow[])
        : rows,
    [isServiceFailuresTab, selectedSfTypes],
  );
  const narrowToReasonScope = useCallback(
    (rows: OrderRow[]): OrderRow[] =>
      showServicePassScopeToggle
        ? (filterRowsByReasonScope(rows, servicePassScope) as OrderRow[])
        : rows,
    [showServicePassScopeToggle, servicePassScope],
  );
  const groupedView = useMemo(() => {
    if (!data) return null;
    let rows = groupedOrderRows(data, activeGrouping);
    if (activeGrouping === 'lc') rows = rows.filter((r) => keepLc(r.dispatcher_key));
    return buildView(narrowToSfTypes(narrowToReasonScope(rows)), data.dates, excludeWeekends, weekly);
  }, [data, activeGrouping, excludeWeekends, weekly, keepLc, narrowToSfTypes, narrowToReasonScope]);
  const scheduleView = useMemo(
    () => (data ? buildView((data.schedule_rows || []).filter((r) => keepLc(r.dispatcher_key)), data.dates, excludeWeekends, weekly) : null),
    [data, excludeWeekends, keepLc, weekly]
  );

  // Current pipeline snapshot (not date-bucketed), scoped to the LC filter.
  const snapshotRows = useMemo<ScheduleSnapshotRow[]>(
    () => (data?.schedule_snapshot || []).filter((r) => keepLc(r.dispatcher_key)),
    [data, keepLc]
  );
  // Apply the LC filter only to the LC adoption roster.
  const adoptionBarRows = useMemo<LcUtilizationRow[]>(
    () => (data?.lc_utilization || []).filter((r) => adoptionGroup === 'invoice_clerks' || keepLc(r.dispatcher_key)),
    [data, keepLc, adoptionGroup]
  );

  const isOrderMetric = currentTab.kind === 'lc' || currentTab.kind === 'customer';
  const currentMetric = currentTab.metric ? withFailureTypeFilter(isOrderMetric ? groupedMetric(currentTab.metric, activeGrouping, 'lc') : currentTab.metric, isServiceFailuresTab ? selectedSfTypes : []) : undefined;
  const currentView = isOrderMetric ? groupedView : currentTab.kind === 'schedule' ? scheduleView : null;
  // Load/error state for the panel the active tab is served by.
  const currentSection = SECTION_FOR_KIND[currentTab.kind];
  const sectionBusy = sectionLoading[currentSection];
  const sectionErr = sectionError[currentSection];

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <FilterBar label="">
        <div className="flex items-center gap-1">
          <div className="w-32">
            <DateTimePicker
              mode="date"
              selectionType="single"
              calendarShortcuts={dateShortcuts}
              value={startDate}
              onChange={(v) => {
                const next = (v as Date | null) ?? null;
                setStartDate(next);
                if (next && endDate && fmtDateKey(next) > fmtDateKey(endDate)) {
                  setEndDate(next);
                }
              }}
              inputVariant="compact"
              labelType="none"
              placeholder="Start"
              maxDate={maxDate}
              autoApply
              className="w-full"
            />
          </div>
          <span className="text-muted">–</span>
          <div className="w-32">
            <DateTimePicker
              mode="date"
              selectionType="single"
              calendarShortcuts={dateShortcuts}
              value={endDate}
              onChange={(v) => {
                const next = (v as Date | null) ?? null;
                setEndDate(next && startDate && fmtDateKey(next) < fmtDateKey(startDate) ? startDate : next);
              }}
              inputVariant="compact"
              labelType="none"
              placeholder="End"
              maxDate={maxDate}
              autoApply
              className="w-full"
            />
          </div>
        </div>

        {/* Daily / Weekly column granularity. */}
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {(['daily', 'weekly'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                viewMode === mode
                  ? 'bg-blue-600 text-white'
                  : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {mode === 'daily' ? 'Daily' : 'Weekly'}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-label">
          <input type="checkbox" checked={excludeWeekends} onChange={(e) => setExcludeWeekends(e.target.checked)} />
          Exclude weekends
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-label">
          <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
          Show details
        </label>
        <button
          type="button"
          onClick={resetFilters}
          title="Reset filters"
          aria-label="Reset filters"
          className="p-1 rounded text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </button>
      </FilterBar>
      <FilterBar label="Filters:" wrap>
        {isOrderMetric && <div className="w-40">
          <MultiSelect
            options={regionOptions}
            value={selectedRegions}
            onChange={setSelectedRegions}
            placeholder="All Regions"
            showClearOption
            clearOptionLabel="All Regions"
            enableSearch
            size="sm"
          />
        </div>}
        {!(currentTab.kind === 'utilization' && adoptionGroup === 'invoice_clerks') && <div className="w-44">
          <MultiSelect
            options={isOrderMetric ? linkedOptions.lcs : lcOptions}
            loading={isOrderMetric && filtersLoading}
            disabled={isOrderMetric && (filtersLoading || filtersError)}
            value={selectedLcs}
            onChange={setSelectedLcs}
            placeholder="All LCs"
            showClearOption
            clearOptionLabel="All LCs"
            enableSearch
            size="sm"
          />
        </div>}
        {isOrderMetric && <div className="w-40">
          <MultiSelect
            options={KEY_STATUS_OPTIONS}
            value={selectedKeyStatuses}
            onChange={setSelectedKeyStatuses}
            placeholder="All Key Status"
            showClearOption
            clearOptionLabel="All Key Status"
            enableSearch
            size="sm"
          />
        </div>}
        {isOrderMetric && <>
          <div className="w-52">
            <MultiSelect options={linkedOptions.customers} value={selectedCustomers} onChange={setSelectedCustomers}
              placeholder="All Customers" showClearOption clearOptionLabel="All Customers" enableSearch size="sm"
              loading={filtersLoading} disabled={filtersLoading || filtersError} />
          </div>
          <div className="w-52">
            <MultiSelect options={linkedOptions.locations} value={selectedLocations} onChange={setSelectedLocations}
              placeholder="All Delivery Locations" showClearOption clearOptionLabel="All Delivery Locations" enableSearch size="sm"
              loading={filtersLoading} disabled={filtersLoading || filtersError} />
          </div>
          {filtersError && <span role="alert" className="text-xs text-red-600">Could not load filter options. Refresh to retry.</span>}
        </>}
      </FilterBar>

      {/* In-progress caveat — its own line below the filter bar, shown only
          while the range reaches into today (deliveries still landing). */}
      {endDate && fmtDateKey(endDate) === fmtDateKey(maxDate) && (
        <div className="px-4 py-1 text-xs italic text-amber-600 dark:text-amber-400 bg-gray-50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
          Today is still in progress — not fully accurate until the next day.
        </div>
      )}

      <OrderMetricSelector
        options={SELECTABLE_TABS}
        metric={selectedMetric}
        onMetricChange={setSelectedMetric}
        group={activeGrouping}
        allowDeliveryLocation={isOrderMetric}
        onGroupChange={setGrouping}
        showGroup={isOrderMetric}
      />

      {/* Description of the selected metric (or the snapshot tab), plus the SF
          Type control — which narrows only this metric, so it sits with it
          rather than in the page-wide filter bar. */}
      {((currentMetric?.description ?? currentTab.description) || isServiceFailuresTab || isServicePassTab) && (
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 px-4 py-1.5 text-xs text-muted bg-gray-50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
          {currentTab.kind === 'utilization' && (
            <div role="group" aria-label="360 Adoption users" className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
              {(['lcs', 'invoice_clerks'] as const).map((group) => (
                <button key={group} type="button" aria-pressed={adoptionGroup === group}
                  onClick={() => { setAdoptionGroup(group); setV1SavesLc(null); }}
                  className={`px-3 py-1 text-xs font-medium whitespace-nowrap border-r last:border-r-0 border-gray-300 dark:border-gray-600 transition-colors ${
                    adoptionGroup === group
                      ? 'bg-blue-600 text-white'
                      : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >{group === 'lcs' ? 'LCs' : 'Invoice Clerks'}</button>
              ))}
            </div>
          )}
          {/* Leads the row it qualifies; no label, the placeholder says it. */}
          {isServiceFailuresTab && (
            <div className="w-44">
              <MultiSelect
                options={SERVICE_FAILURE_TYPE_FILTER_OPTIONS}
                value={selectedSfTypes}
                onChange={setSelectedSfTypes}
                placeholder="All SF Types"
                showClearOption
                clearOptionLabel="All SF Types"
                enableSearch
                size="sm"
              />
            </div>
          )}
          {showServicePassScopeToggle && (
            <div role="group" aria-label="Service failure reason scope" className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
              {SERVICE_PASS_SCOPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={servicePassScope === opt.value}
                  onClick={() => setServicePassScope(opt.value)}
                  className={`px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    servicePassScope === opt.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <span>
            <span className="font-medium text-label">{currentMetric?.label ?? currentTab.label}:</span>{' '}
            {currentTab.kind === 'utilization' && adoptionGroup === 'invoice_clerks'
              ? "GWI 360 share of CMS saves by members of gwi360-InvoiceClerk over the selected date range. Click a clerk under 100% to see their V1 saves."
              : currentMetric?.description ?? currentTab.description}
          </span>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 p-4">
        {sectionBusy ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2.5">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
            <span className="text-sm text-muted">Loading metrics…</span>
          </div>
        ) : sectionErr ? (
          <div className="flex-1 flex items-center justify-center text-red-600 dark:text-red-400">{sectionErr}</div>
        ) : currentTab.kind === 'snapshot' ? (
          <SchedulingSnapshot rows={snapshotRows} onFinalizeCellClick={setFinalizeCell} />
        ) : currentTab.kind === 'utilization' ? (
          <LcAdoptionChart
            rows={adoptionBarRows}
            isDark={isDark}
            rootFontPx={rootFontPx}
            onLcClick={(b) => setV1SavesLc({ key: b.key, name: b.name })}
          />
        ) : currentView && currentMetric ? (
          <MetricMatrixTable
            key={`${currentTab.key}|${grouping}|${bucketSignature(currentView)}`}
            metric={currentMetric}
            view={currentView}
            showDetails={showDetails}
            isDark={isDark}
            axisLabel={isOrderMetric ? groupLabel(activeGrouping) : 'LC'}
            onOpenDetail={openDetail}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">No data for this range/region.</div>
        )}
      </div>

      <DetailModal
        open={detailModal !== null}
        onClose={() => setDetailModal(null)}
        kind={detailModal?.kind ?? 'serviceFailures'}
        title={detailModal?.title ?? ''}
        rows={detailModal?.rows ?? []}
        pros={detailModal?.pros ?? []}
        range={pickerRange}
        onProClick={(pro) => setSelectedPro(pro)}
        onAddServiceFailure={(pro, date, billto) => setSfCreate({ pro, date, billto })}
      />
      {sfCreate && (
        <RescheduleEditModal
          pronumber={sfCreate.pro}
          // THD Reason is hidden without this: the editor gates it on a THD-family
          // bill-to, and these pages are not the pro screen, so the drill row is the
          // only place the bill-to comes from.
          billTo={sfCreate.billto ?? undefined}
          initialServiceFailureDate={sfCreate.date}
          onClose={() => setSfCreate(null)}
          onSaved={() => { setSfCreate(null); fetchData(); }}
        />
      )}
      <LcV1SavesModal
        adoptionGroup={adoptionGroup}
        open={v1SavesLc !== null}
        lc={v1SavesLc}
        range={pickerRange}
        onClose={() => setV1SavesLc(null)}
        onProClick={(pro) => setSelectedPro(pro)}
      />
      <FinalizeDetailModal
        open={finalizeCell !== null}
        cell={finalizeCell}
        range={pickerRange}
        keyStatuses={[]}
        lcs={expandLcs(selectedLcs)}

        onClose={() => setFinalizeCell(null)}
        onProClick={(pro) => setSelectedPro(pro)}
      />
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
};

const LCMetrics: React.FC = () => (
  <ViewedProsProvider>
    <LCMetricsContent />
  </ViewedProsProvider>
);

export default LCMetrics;
